import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { ArrowRight, ArrowLeft, GripVertical, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  if (a.length > 1 && a.every((v, i) => v === i)) {
    [a[0], a[1]] = [a[1], a[0]];
  }
  return a;
}

export default function SequenceQuestion({
  question,
  index,
  total,
  answer,
  onAnswer,
  onNext,
  onBack
}) {
  const [order, setOrder] = useState(() =>
    answer && answer.length === question.tiles.length
      ? answer
      : shuffle(question.tiles.map((_, i) => i))
  );
  const [submitted, setSubmitted] = useState(false);

  const isCorrect = order.every((v, i) => v === i);

  const onDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index)
      return;
    const next = Array.from(order);
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setOrder(next);
    setSubmitted(false);
    onAnswer(next);
  };

  return (
    <div className="max-w-xl mx-auto px-6 py-10 sm:py-16">
      <div className="flex items-center justify-between mb-8">
        <span className="text-xs font-medium tracking-widest uppercase text-primary">
          Question {index + 1} of {total}
        </span>
        <span className="text-xs text-muted-foreground">Drag to reorder</span>
      </div>

      <div className="w-full h-1.5 bg-muted rounded-full mb-10 overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-500"
          style={{ width: `${((index + 1) / total) * 100}%` }}
        />
      </div>

      <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground leading-snug">
        {question.question}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Drag the tiles into the correct chronological order (1 → 4), then hit
        Submit to verify.
      </p>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="sequence">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="mt-6 space-y-3"
            >
              {order.map((tileIdx, pos) => (
                <Draggable
                  key={String(tileIdx)}
                  draggableId={String(tileIdx)}
                  index={pos}
                >
                  {(prov, snapshot) => (
                    <div
                      ref={prov.innerRef}
                      {...prov.draggableProps}
                      {...prov.dragHandleProps}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3.5 rounded-xl border bg-card transition-shadow",
                        snapshot.isDragging
                          ? "border-primary shadow-lg"
                          : "border-border",
                        submitted &&
                          (tileIdx === pos
                            ? "border-emerald-300"
                            : "border-rose-300")
                      )}
                    >
                      <span
                        className={cn(
                          "flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold",
                          submitted
                            ? tileIdx === pos
                              ? "bg-emerald-100 text-emerald-600"
                              : "bg-rose-100 text-rose-600"
                            : "bg-muted text-muted-foreground"
                        )}
                      >
                        {submitted ? (
                          tileIdx === pos ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <X className="w-4 h-4" />
                          )
                        ) : (
                          pos + 1
                        )}
                      </span>
                      <span className="flex-1 text-sm text-foreground/90">
                        {question.tiles[tileIdx]}
                      </span>
                      <GripVertical className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {submitted && (
        <div
          className={cn(
            "mt-5 px-4 py-3 rounded-lg text-sm font-medium",
            isCorrect
              ? "bg-emerald-50 text-emerald-700"
              : "bg-rose-50 text-rose-700"
          )}
        >
          {isCorrect
            ? "Correct — the steps are in the right order."
            : "Not quite. Reorder the tiles and submit again."}
        </div>
      )}

      <div className="mt-8 flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={index === 0}
          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
        >
          <ArrowLeft className="mr-2 w-4 h-4" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setSubmitted(true)}
            variant="outline"
            className="rounded-full border-border"
          >
            Submit
          </Button>
          <Button
            onClick={onNext}
            className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-6"
          >
            {index === total - 1 ? "Finish" : "Next"}
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}